import { useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { FluentProvider, makeStaticStyles, tokens } from '@fluentui/react-components';
import { darkTheme, lightTheme } from './theme';
import { SignupProvider } from './lib/signupState';
import { Landing } from './pages/Landing';
import { Details } from './pages/Details';
import { Terms } from './pages/Terms';
import { Done } from './pages/Done';
import { Saved } from './pages/Saved';
import { ConsentError } from './pages/ConsentError';

// makeStaticStyles can't nest at-rules in object form, so the
// reduced-motion rule is passed as a CSS string.
const useGlobalStyles = makeStaticStyles([
  {
    '*, *::before, *::after': { boxSizing: 'border-box' },
    'html, body': { margin: 0, padding: 0 },
    body: { fontSize: '16px', lineHeight: 1.5, WebkitTextSizeAdjust: '100%' },
  },
  '@media (prefers-reduced-motion: reduce) { *, *::before, *::after { transition-duration: 0.01ms !important; animation-duration: 0.01ms !important; } }',
]);

function usePrefersDark(): boolean {
  const query = '(prefers-color-scheme: dark)';
  const [dark, setDark] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = (e: MediaQueryListEvent) => setDark(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return dark;
}

export function App() {
  useGlobalStyles();
  const dark = usePrefersDark();
  const theme = dark ? darkTheme : lightTheme;

  // Keep the page behind the app (overscroll, safe areas) in the theme colour.
  useEffect(() => {
    document.body.style.backgroundColor = theme.colorNeutralBackground2;
    document.documentElement.style.colorScheme = dark ? 'dark' : 'light';
  }, [theme, dark]);

  return (
    <FluentProvider
      theme={theme}
      // ARM's navy headings (#071952); pale brand tint in dark mode.
      style={{ backgroundColor: tokens.colorNeutralBackground2, ['--tellec-heading' as string]: dark ? '#ebf4f6' : '#071952' }}
    >
      <SignupProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/details" element={<Details />} />
            <Route path="/saved" element={<Saved />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/done" element={<Done />} />
            <Route path="/error" element={<ConsentError />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </SignupProvider>
    </FluentProvider>
  );
}
